import React, { useEffect, useMemo } from "react";
import { TokenSource } from "livekit-client";
import { TokenSourceResponse } from "@livekit/protocol";
import { useSession, SessionProvider } from "@livekit/components-react";
import { logger } from "@/utils";

/**
 * LiveKit token data returned from server API
 */
export interface LiveKitTokenData {
  sessionId: string;
  livekitToken: string;
  livekitUrl: string;
}

/**
 * Props for ChatSessionProvider component
 */
interface ChatSessionProviderProps {
  /** Token data from server API */
  token: LiveKitTokenData;
  /** Child components that need access to SessionProvider context */
  children: React.ReactNode;
}

// Amount of time in milliseonds the system will wait for an agent to join the room, before transitioning to the "failure" state.
const AGENT_CONNECT_TIMEOUT_MS = 20000;

/**
 * Creates LiveKit session and provides SessionProvider context.
 * Only rendered when token exists.
 *
 * This is a pure "plumbing" component that:
 * 1. Creates a TokenSource from server API response
 * 2. Creates a LiveKit session (does NOT start it)
 * 3. Wraps children in SessionProvider for LiveKit hooks access
 */
export function ChatSessionProvider({
  token,
  children
}: ChatSessionProviderProps) {
  // Create token source from API response
  const tokenSource = useMemo(
    () =>
      TokenSource.literal(
        new TokenSourceResponse({
          serverUrl: token.livekitUrl,
          participantToken: token.livekitToken
        })
      ),
    [token.livekitUrl, token.livekitToken]
  );

  // Create session (does NOT start it - ChatRoom handles lifecycle)
  const lkSession = useSession(tokenSource, {
    agentConnectTimeoutMilliseconds: AGENT_CONNECT_TIMEOUT_MS
  });

  useEffect(() => {
    logger.info("ChatSessionProvider mounted");
    return () => {
      logger.info("ChatSessionProvider unmounted");
    };
  }, []);

  return <SessionProvider session={lkSession}>{children}</SessionProvider>;
}
